import { ref, computed, inject, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { CONFIG } from '../config';
import {
  normalizeManagerWire,
  hasManagerContact,
  managerFromPropertyPayload,
  mailtoHref,
  telHref
} from '../../../common/propertyManagerFormatting.js';
import { shouldSyncPublicCmsFromApi } from '../utils/publicCmsSource.js';
import { getPublicPropertyPayload } from '../utils/publicPropertyPayload.js';

let cachedManager = null;
let inFlightManager = null;

/**
 * Published canonical manager contact (locale-independent).
 * Source: GET /v1/property/:locale → property.manager
 *
 * Deployed SSG HTML often embeds `property.manager: {}` from an older publish.
 * That object is not usable contact and must not skip the live API refresh —
 * otherwise the footer keeps site-name fallback and hides email/phone until
 * the next UAT/prod rebuild. Vite has no SSG snapshot, so it already looked fine.
 */
export function usePropertyManagerContact() {
  const initialState = inject('initialState', {});
  const publishedByLocale = inject('publishedPropertyContentByLocale', {});
  const route = useRoute();
  const remoteManager = ref(null);
  const loading = ref(false);

  const locale = computed(() => {
    const lang = route.params?.lang;
    if (lang && CONFIG.SUPPORTED_LOCALES.includes(lang)) return lang;
    return initialState?.locale || CONFIG.DEFAULT_LOCALE;
  });

  const embeddedManager = computed(() => {
    const m = initialState?.property?.manager;
    if (!hasManagerContact(m)) return null;
    return normalizeManagerWire(m);
  });

  const bundledManager = computed(() => {
    const m = publishedByLocale[locale.value]?.property?.manager;
    if (!hasManagerContact(m)) return null;
    return normalizeManagerWire(m);
  });

  const manager = computed(() => {
    if (!shouldSyncPublicCmsFromApi()) {
      const raw = bundledManager.value || embeddedManager.value;
      return raw ? normalizeManagerWire(raw) : null;
    }
    const raw = remoteManager.value || embeddedManager.value || cachedManager;
    return raw ? normalizeManagerWire(raw) : null;
  });

  const hasContact = computed(() => hasManagerContact(manager.value));

  const emailHref = computed(() => mailtoHref(manager.value?.email));
  const phoneHref = computed(() => telHref(manager.value?.phone));

  async function fetchManager() {
    if (!shouldSyncPublicCmsFromApi()) return;
    if (cachedManager) {
      remoteManager.value = cachedManager;
    }
    if (inFlightManager) {
      await inFlightManager;
      if (cachedManager) remoteManager.value = cachedManager;
      return;
    }

    const loc = locale.value;
    loading.value = true;
    inFlightManager = (async () => {
      try {
        const data = await getPublicPropertyPayload(loc);
        const fromApi = managerFromPropertyPayload(data);
        if (fromApi) {
          cachedManager = fromApi;
          remoteManager.value = fromApi;
        }
      } catch {
        // public pages may render without manager until API is available
      } finally {
        inFlightManager = null;
        loading.value = false;
      }
    })();
    await inFlightManager;
  }

  onMounted(() => {
    fetchManager();
  });

  watch(locale, (newLoc, oldLoc) => {
    if (oldLoc === undefined || newLoc === oldLoc) return;
    fetchManager();
  });

  return {
    manager,
    hasContact,
    emailHref,
    phoneHref,
    loading,
    refetch: fetchManager
  };
}
